import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Loader2, Upload, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

const EMPTY = { consultant_name: "", firm: "", phone: "", email: "", website: "", logo_data_url: null };
const FIELDS = [
  ["consultant_name", "Consultant name", "e.g. Ar. Priya Sharma"],
  ["firm", "Firm / Brand", "e.g. Vaastu Just 4 U"],
  ["phone", "Phone", "+91 98xxx xxxxx"],
  ["email", "Email", "you@example.com"],
  ["website", "Website", "www.example.com"],
];

// Downscale logo to max 240px tall and cap ~300 KB
async function processLogo(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const scale = Math.min(1, 240 / img.naturalHeight, 800 / img.naturalWidth);
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    let out = c.toDataURL("image/png");
    if (out.length > 300_000) out = c.toDataURL("image/jpeg", 0.85);
    if (out.length > 300_000) throw new Error("Logo too large even after resizing");
    return out;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function BrandingDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/profile/branding")
      .then(({ data }) => setForm({ ...EMPTY, ...data }))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleLogo = async (file) => {
    try {
      const logo = await processLogo(file);
      setForm((f) => ({ ...f, logo_data_url: logo }));
    } catch (e) {
      toast.error(e.message || "Could not read logo");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/profile/branding", form);
      toast.success("Branding saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg" data-testid="branding-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-amber-500" /> Report branding
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Shown in the header of every print-ready PDF you export.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 p-4 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mt-1">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k} className={`space-y-1.5 ${k === "consultant_name" || k === "firm" ? "col-span-2" : ""}`}>
                <Label htmlFor={`brand-${k}`} className="text-xs text-slate-400">{label}</Label>
                <Input id={`brand-${k}`} data-testid={`branding-${k}-input`} value={form[k] || ""} onChange={set(k)} placeholder={ph}
                  className="bg-slate-950 border-slate-800 text-slate-100 h-9" />
              </div>
            ))}

            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-slate-400">Logo (PNG/JPG, auto-resized)</Label>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" data-testid="branding-logo-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogo(f); e.target.value = ""; }} />
              <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-800 bg-slate-950/60">
                {form.logo_data_url ? (
                  <img src={form.logo_data_url} alt="Logo" data-testid="branding-logo-preview" className="h-12 max-w-[160px] object-contain bg-white rounded p-1" />
                ) : (
                  <div className="h-12 w-24 rounded bg-slate-900 border border-dashed border-slate-700 flex items-center justify-center font-mono text-[10px] text-slate-500">No logo</div>
                )}
                <Button type="button" size="sm" variant="outline" data-testid="branding-upload-logo-button" onClick={() => fileRef.current?.click()}
                  className="h-8 bg-slate-900 border-slate-800 hover:bg-slate-800 text-slate-100">
                  <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload
                </Button>
                {form.logo_data_url && (
                  <button type="button" data-testid="branding-remove-logo-button" onClick={() => setForm((f) => ({ ...f, logo_data_url: null }))}
                    className="text-slate-400 hover:text-red-300"><X className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-slate-900 border-slate-800 hover:bg-slate-800 text-slate-100">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="branding-save-button" className="bg-amber-500 hover:bg-amber-400 text-slate-950">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save branding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
